import { createContext, useContext, type ReactNode } from "react";
import { useRouter } from "next/router";
import { ListData } from "@/reports/components/ReadingSkillsDataList";

import { type DateRangeTranslatedValues } from "@/reports/teacher/common/PageHeaderContainer/components/InsightsPageFilters/DateRangeFilter/DateRangeFilter.utils";
import { getMinMaxDates } from "@/reports/admin/AdminInsightsReportFilters/DateRangeFilter/DateRangeFilter.utils";
import { type Classroom } from "@/types/models/Classroom.interface";
import { type User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import useSearchParamsUpdate, {
  type SearchParamsInterface,
} from "@/utils/useSearchParamsUpdate";
import getMinimumDateForReports from "@/reports/utils/getMinimumDateForReports";
import {
  IS_INCLUSIVE_BETW_JULY1_TO_SEPT_15,
  START_OF_7_DAYS,
  START_OF_30_DAYS,
  START_OF_90_DAYS,
  TODAY,
  LAST_YEAR_START_DATE,
  LAST_YEAR_END_DATE,
} from "@/reports/constants";
import { getSelectedQueryValue } from "../../../utils/getSelectedQueryValue";

const InsightsSharedContext =
  createContext<InsightsSharedContextInterface | null>(null);

interface InsightsSharedContextProviderProps {
  children: ReactNode;
  classrooms: Classroom[];
  loadingClasses: boolean;
  isVeiled: boolean;
  readingSkillsInitialProps?: ListData | undefined;
}
export interface Filters {
  classroomUniqueId: string | undefined;
  studentUniqueId: string | undefined;
  dateRange: [string, string];
  onlyAssignedContent: boolean | null;
  readingLevel: string | undefined;
  schoolUniqueId?: string | undefined;
}

export const InsightsSharedContextProvider = ({
  children,
  classrooms,
  loadingClasses,
  isVeiled,
  readingSkillsInitialProps,
}: InsightsSharedContextProviderProps) => {
  const router = useRouter();
  const { query, asPath: returnUrl } = router;
  const { data: user } = useUser();
  const { activeELALicense } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });

  const handleSearchParamsUpdate = useSearchParamsUpdate();

  const dateRangeTranslatedValues: DateRangeTranslatedValues = [
    {
      value: "july_to_date",
      min: getMinimumDateForReports(START_OF_30_DAYS),
      max: TODAY,
      label: "July to date",
    },
    {
      value: "90_days",
      min: START_OF_90_DAYS,
      max: TODAY,
      label: "Last 90 days",
    },
    {
      value: "30_days",
      min: START_OF_30_DAYS,
      max: TODAY,
      label: "Last 30 days",
    },
    { value: "7_days", min: START_OF_7_DAYS, max: TODAY, label: "Last 7 days" },
    {
      value: "last_school_year",
      min: LAST_YEAR_START_DATE,
      max: LAST_YEAR_END_DATE,
      label: "Last school year",
    },
  ];

  const dateRangeMenuOptions = dateRangeTranslatedValues.map((dateRange) => {
    return { value: dateRange.value, label: dateRange.label };
  });

  let dateRangeFilterDefaultOption = {
    value: "july_to_date",
    label: "July to date",
  };

  if (IS_INCLUSIVE_BETW_JULY1_TO_SEPT_15) {
    dateRangeFilterDefaultOption = { value: "90_days", label: "Last 90 days" };
    // remove aug to date option
    dateRangeMenuOptions.shift();
  }

  const selectedClassroom = getSelectedQueryValue(query["classroom"]);
  const selectedStudent = getSelectedQueryValue(query["student"]);
  const selectedDateRange = getSelectedQueryValue(query["date_range"]);
  const selectedOnlyAssignedContent = getSelectedQueryValue(
    query["only_assigned_content"],
  );
  const selectedReadingLevel = getSelectedQueryValue(query["reading_level"]);
  const selectedMinDate = getSelectedQueryValue(query["min_date"]);
  const selectedMaxDate = getSelectedQueryValue(query["max_date"]);

  const getClassroom = (classroomUniqueId: string): Classroom | undefined => {
    return classrooms.find(
      (classroom: Classroom) => classroom.unique_id === classroomUniqueId,
    );
  };

  const selectedFilters: Filters = {
    classroomUniqueId: selectedClassroom,
    studentUniqueId: selectedStudent,
    dateRange: getMinMaxDates(
      selectedDateRange,
      dateRangeTranslatedValues,
      selectedMinDate,
      selectedMaxDate,
    ),
    onlyAssignedContent: selectedOnlyAssignedContent
      ? selectedOnlyAssignedContent === "true"
      : null,
    readingLevel: selectedReadingLevel,
  };

  const navigateToFirstClassroom = () => {
    const firstClassroomUniqueId = classrooms[0]?.unique_id;
    if (firstClassroomUniqueId) {
      handleSearchParamsUpdate({
        setParams: [{ name: "classroom", value: firstClassroomUniqueId }],
      });
    }
  };

  return (
    <InsightsSharedContext.Provider
      value={{
        activeELALicense: isVeiled || activeELALicense,
        classrooms,
        loadingClasses,
        handleSearchParamsUpdate,
        user,
        dateRangeMenuOptions,
        dateRangeFilterDefaultOption,
        selectedFilters,
        getClassroom,
        returnUrl,
        readingSkillsInitialProps,
        navigateToFirstClassroom,
      }}
    >
      {children}
    </InsightsSharedContext.Provider>
  );
};

export interface InsightsSharedContextInterface {
  returnUrl: string;
  classrooms: Classroom[];
  loadingClasses?: boolean;
  activeELALicense?: boolean;
  handleSearchParamsUpdate: ({
    setParams,
    deleteParams,
  }: SearchParamsInterface) => void;
  getClassroom: (classroomUniqueId: string) => Classroom | undefined;
  user: Partial<User>;
  dateRangeMenuOptions: Array<{ value: string; label: string }>;
  dateRangeFilterDefaultOption: { value: string; label: string };
  selectedFilters: Filters;
  readingSkillsInitialProps?: ListData | undefined;
  navigateToFirstClassroom: () => void;
}

const useInsightsSharedContext = (): InsightsSharedContextInterface => {
  const insightsSharedContext = useContext(InsightsSharedContext);

  if (insightsSharedContext === null) {
    throw new Error(
      "insightsSharedContext must be used within an InsightsSharedContextProvider.",
    );
  }

  return insightsSharedContext;
};

export default useInsightsSharedContext;
